import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../context/ShopContext";
import { imgSrc } from "../pages/Portfolio";

const emptyForm = {
  name_it: "", name_en: "", slug: "", sku: "",
  price: "", regular_price: "", on_sale: false,
  price_suffix_it: "", price_suffix_en: "", subscription_interval: "",
  short_description_it: "", short_description_en: "",
  description_it: "", description_en: "",
  image: "", categories: [], tags: "",
  is_digital: false, requires_license: false,
};

const productToForm = (p) => ({
  name_it: p.name?.it || "",
  name_en: p.name?.en || "",
  slug: p.slug || "",
  sku: p.sku || "",
  price: p.price != null ? String(p.price) : "",
  regular_price: p.regularPrice != null ? String(p.regularPrice) : "",
  on_sale: !!p.onSale,
  price_suffix_it: p.priceSuffix?.it || "",
  price_suffix_en: p.priceSuffix?.en || "",
  subscription_interval: p.subscription?.interval || "",
  short_description_it: p.shortDescription?.it || "",
  short_description_en: p.shortDescription?.en || "",
  description_it: p.description?.it || "",
  description_en: p.description?.en || "",
  image: p.image || "",
  categories: p.categories || [],
  tags: (p.tags || []).join(", "),
  is_digital: !!p.is_digital,
  requires_license: !!p.requires_license,
});

const ProductsAdmin = ({ authHeaders }) => {
  const [products, setProducts] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/products`, authHeaders()),
        axios.get(`${API}/categories`),
      ]);
      setProducts(prodRes.data);
      setAllCats(catRes.data);
    } catch (err) {
      toast.error("Errore caricamento prodotti");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm(productToForm(p));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const toggleCat = (slug) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug)
        ? f.categories.filter((c) => c !== slug)
        : [...f.categories, slug],
    }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, fd, authHeaders());
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Immagine caricata");
    } catch {
      toast.error("Errore caricamento immagine");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name_it.trim()) {
      toast.error("Il nome (IT) è obbligatorio");
      return;
    }
    const price = parseFloat(String(form.price).replace(",", "."));
    if (isNaN(price) || price < 0) {
      toast.error("Prezzo non valido");
      return;
    }
    if (form.categories.length === 0) {
      toast.error("Seleziona almeno una categoria");
      return;
    }
    const regular = parseFloat(String(form.regular_price).replace(",", "."));
    const payload = {
      ...form,
      price,
      regular_price: isNaN(regular) ? null : regular,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      if (editingId != null) {
        await axios.put(`${API}/admin/products/${editingId}`, payload, authHeaders());
        toast.success("Prodotto aggiornato");
      } else {
        await axios.post(`${API}/admin/products`, payload, authHeaders());
        toast.success("Prodotto creato");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Eliminare "${p.name?.it}"? Verranno rimossi anche i file digitali associati.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, authHeaders());
      toast.success("Prodotto eliminato");
      if (editingId === p.id) cancelEdit();
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  return (
    <section className="idm-admin-section" data-testid="admin-products">
      <h3 className="idm-admin-section-title">Gestione prodotti</h3>
      <p className="idm-admin-section-sub">
        Crea, modifica ed elimina i prodotti dello shop. I file digitali e la documentazione
        si gestiscono dalla scheda "File digitali". Attiva "Richiede licenza" per generare
        automaticamente una chiave di licenza a ogni acquisto.
      </p>

      <form className="idm-admin-form" onSubmit={handleSave} data-testid="admin-product-form">
        <h3>
          {editingId != null ? <><Pencil size={16} /> Modifica prodotto</> : <><Plus size={16} /> Aggiungi nuovo prodotto</>}
          {editingId != null && (
            <button type="button" className="idm-admin-cancel" onClick={cancelEdit} data-testid="admin-product-cancel">
              <X size={14} /> Annulla
            </button>
          )}
        </h3>
        <div className="idm-form-grid">
          <div className="idm-field half">
            <label>Nome (IT) *</label>
            <input value={form.name_it} onChange={(e) => setForm({ ...form, name_it: e.target.value })} data-testid="admin-product-name-it" />
          </div>
          <div className="idm-field half">
            <label>Nome (EN)</label>
            <input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="admin-product-name-en" />
          </div>
          <div className="idm-field half">
            <label>Slug (URL prodotto, vuoto = automatico dal nome)</label>
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="es. perfex-crm-modulo-x" data-testid="admin-product-slug" />
          </div>
          <div className="idm-field half">
            <label>SKU</label>
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="es. IDM-PFX001" data-testid="admin-product-sku" />
          </div>
          <div className="idm-field half">
            <label>Prezzo (€) *</label>
            <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="es. 19.90" inputMode="decimal" data-testid="admin-product-price" />
          </div>
          <div className="idm-field half">
            <label>Prezzo pieno (€, mostrato barrato se in offerta)</label>
            <input value={form.regular_price} onChange={(e) => setForm({ ...form, regular_price: e.target.value })} placeholder="es. 29.90" inputMode="decimal" data-testid="admin-product-regular-price" />
          </div>
          <div className="idm-field half">
            <label>Suffisso prezzo (IT)</label>
            <input value={form.price_suffix_it} onChange={(e) => setForm({ ...form, price_suffix_it: e.target.value })} placeholder="es. / anno" data-testid="admin-product-suffix-it" />
          </div>
          <div className="idm-field half">
            <label>Suffisso prezzo (EN)</label>
            <input value={form.price_suffix_en} onChange={(e) => setForm({ ...form, price_suffix_en: e.target.value })} placeholder="e.g. / year" data-testid="admin-product-suffix-en" />
          </div>
          <div className="idm-field half">
            <label>Abbonamento</label>
            <select
              className="idm-select"
              value={form.subscription_interval}
              onChange={(e) => setForm({ ...form, subscription_interval: e.target.value })}
              data-testid="admin-product-subscription"
            >
              <option value="">Acquisto singolo</option>
              <option value="month">Rinnovo mensile</option>
              <option value="year">Rinnovo annuale</option>
            </select>
          </div>
          <div className="idm-field half">
            <label>Immagine</label>
            <div className="idm-admin-imgrow">
              <label className="idm-upload-btn">
                {uploading ? <Loader2 size={14} className="idm-spin" /> : <Upload size={14} />}
                {uploading ? " Caricamento..." : " Carica file"}
                <input type="file" accept="image/*" onChange={handleUpload} hidden data-testid="admin-product-upload" />
              </label>
              {form.image && <img src={imgSrc(form.image)} alt="" className="idm-admin-thumb" />}
            </div>
          </div>
          <div className="idm-field half">
            <label>Descrizione breve (IT)</label>
            <textarea rows={3} value={form.short_description_it} onChange={(e) => setForm({ ...form, short_description_it: e.target.value })} data-testid="admin-product-short-it" />
          </div>
          <div className="idm-field half">
            <label>Descrizione breve (EN)</label>
            <textarea rows={3} value={form.short_description_en} onChange={(e) => setForm({ ...form, short_description_en: e.target.value })} data-testid="admin-product-short-en" />
          </div>
          <div className="idm-field half">
            <label>Descrizione completa (IT)</label>
            <textarea rows={6} value={form.description_it} onChange={(e) => setForm({ ...form, description_it: e.target.value })} data-testid="admin-product-desc-it" />
          </div>
          <div className="idm-field half">
            <label>Descrizione completa (EN)</label>
            <textarea rows={6} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} data-testid="admin-product-desc-en" />
          </div>
          <div className="idm-field half">
            <label>Categorie *</label>
            <div className="idm-tax-cats" data-testid="admin-product-cats">
              {allCats.map((c) => (
                <label key={c.slug}>
                  <input
                    type="checkbox"
                    checked={form.categories.includes(c.slug)}
                    onChange={() => toggleCat(c.slug)}
                    data-testid={`admin-product-cat-${c.slug}`}
                  />
                  {c.name.it}
                </label>
              ))}
            </div>
          </div>
          <div className="idm-field half">
            <label>Tag (separati da virgola)</label>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="es. prestashop, crm, api"
              data-testid="admin-product-tags"
            />
          </div>
          <div className="idm-field half">
            <label>Opzioni</label>
            <div className="idm-tax-cats">
              <label>
                <input
                  type="checkbox"
                  checked={form.on_sale}
                  onChange={(e) => setForm({ ...form, on_sale: e.target.checked })}
                  data-testid="admin-product-onsale"
                />
                In offerta (badge SALE + prezzo pieno barrato)
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.is_digital}
                  onChange={(e) => setForm({ ...form, is_digital: e.target.checked })}
                  data-testid="admin-product-digital"
                />
                Prodotto digitale (download)
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.requires_license}
                  onChange={(e) => setForm({ ...form, requires_license: e.target.checked })}
                  data-testid="admin-product-license"
                />
                Richiede licenza (generata automaticamente all'acquisto)
              </label>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <button type="submit" className="idm-btn-yellow" style={{ animation: "none" }} disabled={saving} data-testid="admin-product-save">
            {saving ? <Loader2 size={16} className="idm-spin" /> : editingId != null ? "Salva modifiche" : "Aggiungi prodotto"}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="idm-loading"><span className="idm-spinner" /></div>
      ) : (
        <div className="idm-admin-list" data-testid="admin-product-list">
          {products.map((p) => (
            <div className="idm-admin-row" key={p.id} data-testid={`admin-product-row-${p.id}`}>
              {p.image ? (
                <img src={imgSrc(p.image)} alt="" loading="lazy" />
              ) : (
                <div className="idm-admin-noimg" />
              )}
              <div className="idm-admin-row-info">
                <strong>{p.name?.it}</strong>
                <span>
                  €{p.price}{p.priceSuffix?.it ? ` ${p.priceSuffix.it}` : ""}
                  {p.onSale ? " · SALE" : ""}
                  {p.is_digital ? " · digitale" : ""}
                  {p.requires_license ? " · licenza" : ""}
                  {" · "}{(p.categories || []).join(", ")}
                </span>
              </div>
              <div className="idm-admin-row-actions">
                <button onClick={() => startEdit(p)} aria-label="Modifica" data-testid={`admin-product-edit-${p.id}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => handleDelete(p)} className="danger" aria-label="Elimina" data-testid={`admin-product-delete-${p.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProductsAdmin;
